import { makePaginationUrl } from '../../utils/utils';
import { PaginatedApiResponse, PaginationParams } from '../types/common';
import { BuilderType, portalCacheTags } from './portalApiHelpers';


export type Asset = {
  id: number;
  name: string;
  asset_type: string;
  created_at: string;
  updated_at: string;
  compartment_id: null;
  salt: string;
  alias: string;
  account_id: number;
  simulated: boolean;
};
export type AssetSearch = {
  results: Asset[];
  meta: {};
};

export type AssetByName = Pick<Asset, 'name' | 'alias' | 'asset_type' | 'account_id'> & {
  account_name: string;
};

export type ValidateAssetPoliciesRequest = {
  asset_names: string[];
  policies: string[];
};

export type ValidateAssetPoliciesResponse = Record<string, boolean>;

export type AssetType =
  | 'battery'
  | 'charger'
  | 'gateway'
  | 'vanadium_bess'
  | 'leadacid_bess'
  | 'power_logger';



export const assetsApi = (builder: BuilderType) => ({

  getAssetsByNames: builder.query<AssetByName[], string[]>({
    query: (names) => ({
      url: '/service/assets/by-names',
      method: 'POST',
      body: { ids: names },
    }),
    providesTags: [portalCacheTags.ASSETS],
  }),

  validateAssetPolicies: builder.query<
    ValidateAssetPoliciesResponse,
    ValidateAssetPoliciesRequest
  >({
    query: (body) => ({
      url: '/service/assets/validate-policies',
      method: 'POST',
      body,
    }),
  }),

  getAssets: builder.query<any, { filterAlias?: string; filterType?: string } | undefined>({
    query: (queryParams) =>
      `service/assets.json${queryParams ? `?${new URLSearchParams(queryParams).toString()}` : ''}`,
    providesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_GET_ASSETS), */
  }),

  createAsset: builder.mutation<any, Asset>({
    query: (asset) => ({
      method: 'POST',
      url: `/service/assets.json`,
      body: asset,
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_CREATE_ASSET), */
  }),

  searchAsset: builder.query<AssetSearch, Record<string, any>>({
    query: (query?: Record<string, string>) => {
      const queryString = new URLSearchParams(query ?? {}).toString();

      return `service/assets/search?${queryString}`;
    },
    providesTags: [portalCacheTags.ASSETS],
  }),

  searchAssetPaginated: builder.query<PaginatedApiResponse<AssetSearch>, PaginationParams>({
    query: (params) => makePaginationUrl(`/service/assets/search`, params),
    providesTags: [portalCacheTags.ASSETS],
  }),

  getAssetsPaginated: builder.query<PaginatedApiResponse<AssetSearch>, PaginationParams>({
    query: (params) => makePaginationUrl(`/service/assets/paginated`, params),
    providesTags: [portalCacheTags.ASSETS],
  }),

  // prettier-ignore
  createAssetWithConfiguration: builder.mutation<any,{ asset: Asset; configuration: any }>({
    query: ({ asset, configuration }) => ({
      method: "POST",
      url: `/service/assets/with-config.json`,
      body: { ...asset, configuration },
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_CREATE_ASSET), */
  }),

  updateAsset: builder.mutation<Asset, Asset>({
    query: (asset) => ({
      method: 'PUT',
      url: `/service/assets/${asset.id}.json`,
      body: asset,
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_UPDATE_ASSET), */
  }),

  updateAssetAlias: builder.mutation<Asset, Asset>({
    query: (asset) => ({
      method: 'PUT',
      url: `/service/assets/alias/${asset.id}.json`,
      body: asset,
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_UPDATE_ASSET), */
  }),

  updateAssetAccount: builder.mutation<Asset, Asset>({
    query: (asset) => ({
      method: 'PUT',
      url: `/service/assets/account/${asset.id}.json`,
      body: asset,
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_UPDATE_ASSET), */
  }),

  moveAssetList: builder.mutation<Asset,{ destinationAccountId: string; ids: string[] }>({
    query: ({ destinationAccountId, ids }) => ({
      method: "POST",
      url: "/service/assets/move-list",
      body: {
        destination_id: destinationAccountId,
        ids: ids,
      },
    }),
    invalidatesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_MOVE_ASSET_LIST), */
  }),

  getAssetIds: builder.query<[number, string][], AssetType>({
    query: (type) => `service/assets/ids${type ? `?asset_type=${type}` : ''}`,
    providesTags: [portalCacheTags.ASSETS],
  }),

  getAssetsSummary: builder.query<Asset[], { asset_type: AssetType } | undefined>({
    query: (params) =>
      `service/assets/summary${params ? `?${new URLSearchParams(params).toString()}` : ''}`,
    providesTags: [portalCacheTags.ASSETS],
  }),

  getAsset: builder.query<any, string | undefined>({
    query: (id) => `service/assets/${id}`,
    providesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_GET_ASSETS), */
  }),

  getAssetByName: builder.query<any, string | undefined>({
    query: (name) => `service/assets/by-name/${name}`,
    providesTags: [portalCacheTags.ASSETS],
    /* onQueryStarted: legacyFulfill(PORTAL_RECEIVED_GET_ASSETS), */
  }),
});
